// ============================================================================
// REMINDER TEMPLATES ADMIN PAGE — Gestión de plantillas de recordatorio
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Save, X, Loader2, Building2, AlertCircle, RefreshCw, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { reminderTemplatesApi, type ReminderTemplate, type TemplateSiteAssignment } from '../../api/reminder-templates';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';

const PAGE_SIZE = 20;

// Variables disponibles en las plantillas
const TEMPLATE_VARS = [
  { key: '{{nombre}}',      label: 'Nombre del paciente' },
  { key: '{{fecha}}',       label: 'Fecha del recordatorio' },
  { key: '{{hora}}',        label: 'Hora de envío' },
  { key: '{{tratamiento}}', label: 'Nombre del tratamiento' },
  { key: '{{presupuesto}}', label: 'Nº de presupuesto' },
  { key: '{{deuda}}',       label: 'Deuda pendiente en €' },
];

// Preview del mensaje con datos de ejemplo
function MessagePreview({ template }: { template: string }) {
  const preview = template
    .replace(/\{\{nombre\}\}/gi, 'Juan García')
    .replace(/\{\{fecha\}\}/gi, '20/05/2026')
    .replace(/\{\{hora\}\}/gi, '10:00')
    .replace(/\{\{tratamiento\}\}/gi, 'Ortodoncia')
    .replace(/\{\{presupuesto\}\}/gi, 'MLG-2026-0001')
    .replace(/\{\{deuda\}\}/gi, '450,00€');
  return (
    <div className="p-3 bg-green-50 border border-green-100 rounded-xl text-sm text-gray-700 whitespace-pre-wrap">
      {preview || <span className="text-gray-400 italic">Vista previa del mensaje...</span>}
    </div>
  );
}

export function ReminderTemplatesAdminPage() {
  const toast = useToast();
  const [items, setItems] = useState<ReminderTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ReminderTemplate | null>(null);
  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean; item: ReminderTemplate | null; action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  const [assignmentModal, setAssignmentModal] = useState<{
    isOpen: boolean; item: ReminderTemplate | null;
    sites: TemplateSiteAssignment[]; selectedIds: string[];
    isLoading: boolean; isSaving: boolean;
  }>({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await reminderTemplatesApi.getAll(showInactive);
      setItems(data);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron cargar las plantillas');
    } finally { setIsLoading(false); }
  }, [showInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = items.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.messageTemplate.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => {
    setEditingItem(null); setFormName(''); setFormMessage(''); setFormError(null); setIsModalOpen(true);
  };
  const openEdit = (item: ReminderTemplate) => {
    setEditingItem(item); setFormName(item.name); setFormMessage(item.messageTemplate); setFormError(null); setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setFormError('El nombre es obligatorio'); return; }
    if (!formMessage.trim()) { setFormError('El mensaje es obligatorio'); return; }
    setIsSaving(true); setFormError(null);
    try {
      if (editingItem) {
        await reminderTemplatesApi.update(editingItem.id, { name: formName.trim(), messageTemplate: formMessage.trim() });
        toast.success('Guardado', 'Plantilla actualizada');
      } else {
        await reminderTemplatesApi.create({ name: formName.trim(), messageTemplate: formMessage.trim() });
        toast.success('Creada', 'Plantilla creada correctamente');
      }
      setIsModalOpen(false);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally { setIsSaving(false); }
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await reminderTemplatesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivada', `"${confirmDialog.item.name}" desactivada`);
      } else {
        await reminderTemplatesApi.reactivate(confirmDialog.item.id);
        toast.success('Activada', `"${confirmDialog.item.name}" reactivada`);
      }
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally { setIsProcessing(false); }
  };

  const handleOpenAssignment = async (item: ReminderTemplate) => {
    setAssignmentModal({ isOpen: true, item, sites: [], selectedIds: [], isLoading: true, isSaving: false });
    try {
      const sites = await reminderTemplatesApi.getSiteAssignments(item.id);
      setAssignmentModal(prev => ({
        ...prev, sites, selectedIds: sites.filter(s => s.isAssigned).map(s => s.id), isLoading: false,
      }));
    } catch { setAssignmentModal(prev => ({ ...prev, isLoading: false })); }
  };

  const handleSaveAssignment = async () => {
    if (!assignmentModal.item) return;
    setAssignmentModal(prev => ({ ...prev, isSaving: true }));
    try {
      await reminderTemplatesApi.updateSiteAssignments(assignmentModal.item.id, assignmentModal.selectedIds);
      toast.success('Guardado', 'Asignación de centros actualizada');
      setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false });
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar');
      setAssignmentModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  const insertVar = (v: string) => {
    setFormMessage(prev => prev + v);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plantillas de recordatorio</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configura los mensajes que se enviarán por WhatsApp como recordatorio.
          </p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl transition-colors">
          <Plus className="h-4 w-4" /> Nueva plantilla
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
        <input type="text" placeholder="Buscar..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 max-w-xs px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer ml-auto">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-blue-600" />
          Mostrar inactivas
        </label>
        <button onClick={loadData} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
          </div>
        ) : paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <MessageSquare className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">No hay plantillas</p>
            <p className="text-xs mt-1">Crea la primera con el botón "Nueva plantilla"</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mensaje</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Centros</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map(item => (
                  <tr key={item.id} className={`hover:bg-gray-50 transition-colors ${!item.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                    <td className="px-6 py-4 text-gray-500 max-w-xs truncate text-xs font-mono">{item.messageTemplate}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleOpenAssignment(item)}
                        className="text-xs text-[#008DD0] hover:underline flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {item.sitesCount ?? 0} centro{item.sitesCount !== 1 ? 's' : ''}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(item)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                          <Edit2 className="h-3.5 w-3.5" /> Editar
                        </button>
                        <button onClick={() => setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' })}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            item.isActive
                              ? 'text-red-600 bg-white border-red-200 hover:bg-red-50'
                              : 'text-green-700 bg-white border-green-200 hover:bg-green-50'
                          }`}>
                          {item.isActive ? <><X className="h-3.5 w-3.5" /> Desactivar</> : <><RefreshCw className="h-3.5 w-3.5" /> Activar</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700">Página {page} de {totalPages}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages}
                    className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear/editar */}
      <Modal isOpen={isModalOpen} onClose={() => !isSaving && setIsModalOpen(false)}
        title={editingItem ? 'Editar plantilla' : 'Nueva plantilla de recordatorio'}
        subtitle="Usa {{nombre}}, {{fecha}}, {{hora}} y {{tratamiento}} como variables" size="lg">
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 flex-1">{formError}</p>
              <button onClick={() => setFormError(null)} className="p-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input type="text" value={formName} onChange={e => setFormName(e.target.value)} maxLength={100}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]"
              placeholder="Ej: Recordatorio revisión anual" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Mensaje *</label>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {TEMPLATE_VARS.map(v => (
                  <button key={v.key} onClick={() => insertVar(v.key)} type="button"
                    title={v.label}
                    className="text-xs px-2 py-0.5 bg-blue-50 text-[#008DD0] rounded-lg hover:bg-blue-100 transition-colors font-mono">
                    {v.key}
                  </button>
                ))}
              </div>
            </div>
            <textarea value={formMessage} onChange={e => setFormMessage(e.target.value)} rows={4} maxLength={1000}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0] resize-none"
              placeholder="Hola {{nombre}}, te recordamos tu cita del {{fecha}} a las {{hora}}..." />
            <p className="mt-1 text-xs text-gray-400">{formMessage.length}/1000 caracteres</p>
          </div>
          {formMessage && (
            <div>
              <p className="text-xs text-gray-500 mb-1 font-medium">Vista previa:</p>
              <MessagePreview template={formMessage} />
            </div>
          )}
        </div>
        <ModalFooter>
          <button type="button" onClick={() => !isSaving && setIsModalOpen(false)} disabled={isSaving}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button type="button" onClick={handleSave} disabled={isSaving}
            className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
            {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
          </button>
        </ModalFooter>
      </Modal>

      {/* Modal asignación centros */}
      <Modal isOpen={assignmentModal.isOpen}
        onClose={() => !assignmentModal.isSaving && setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
        title="Centros asignados"
        subtitle={assignmentModal.item ? `"${assignmentModal.item.name}" estará disponible en los centros seleccionados` : ''}>
        {assignmentModal.isLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {assignmentModal.sites.map(site => (
              <label key={site.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                <input type="checkbox" checked={assignmentModal.selectedIds.includes(site.id)}
                  onChange={() => setAssignmentModal(prev => ({
                    ...prev,
                    selectedIds: prev.selectedIds.includes(site.id)
                      ? prev.selectedIds.filter(id => id !== site.id)
                      : [...prev.selectedIds, site.id],
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="font-medium text-gray-800 text-sm flex-1 truncate">{site.name}</span>
                <span className="text-xs text-gray-400 font-mono shrink-0">{site.code}</span>
                {assignmentModal.selectedIds.includes(site.id) && <span className="text-xs text-blue-600 font-medium shrink-0">Asignado</span>}
              </label>
            ))}
          </div>
        )}
        <ModalFooter>
          <button type="button" onClick={() => setAssignmentModal({ isOpen: false, item: null, sites: [], selectedIds: [], isLoading: false, isSaving: false })}
            disabled={assignmentModal.isSaving}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button type="button" onClick={handleSaveAssignment} disabled={assignmentModal.isSaving || assignmentModal.isLoading}
            className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
            {assignmentModal.isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar plantilla?' : '¿Activar plantilla?'}
        message={confirmDialog.action === 'deactivate' ? `"${confirmDialog.item?.name}" será desactivada.` : `"${confirmDialog.item?.name}" será reactivada.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default ReminderTemplatesAdminPage;
